import { getShareFraction, normalizeShareMap } from '../services/itemShares';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Lock, SlidersHorizontal, Users, X } from 'lucide-react';
import { WhoHadThisParticipant, matchesWhoHadThisParticipant, getWhoHadThisParticipantCanonicalKey } from './WebWhoHadThisSheet';
import { WebItemIconAvatar } from './WebItemIconAvatar';
import { resolveWebParticipantIdentity, getParticipantInitials } from '../utils/webParticipantAvatar';
import { useSheetLifecycle } from '../hooks/useSheetLifecycle';
import { triggerUiHaptic } from '../utils/webHaptics';

export type WebHowWasThisSharedSheetProps = {
    visible: boolean;
    onClose: () => void;
    item: {
        id: string;
        name: string;
        amount: number;
        iconKey?: string;
        shares?: Record<string, number | { fraction: number; name?: string; mode?: string; isLocked?: boolean }>;
    } | null;
    userPhone: string;
    organizerPhone?: string;
    organizerName?: string;
    organizerPhotoURL?: string | null;
    session?: any;
    participants: WhoHadThisParticipant[];
    onJoinSplit: (itemId: string, portionAmount: number, forceEqual?: boolean) => void;
    onClaimFull: (itemId: string) => void;
    onShareWithSomeone: (itemId: string) => void;
    getItemIconEmoji?: (iconKey?: string, name?: string) => string;
};

export const WebHowWasThisSharedSheet: React.FC<WebHowWasThisSharedSheetProps> = ({
    visible,
    onClose,
    item,
    userPhone,
    organizerPhone,
    organizerName,
    organizerPhotoURL,
    session,
    participants,
    onJoinSplit,
    onClaimFull,
    onShareWithSomeone,
}) => {
    const lastItemRef = useRef(item);
    if (item) {
        lastItemRef.current = item;
    }
    const currentItem = item || lastItemRef.current;
    const itemAmount = Math.max(0, Number(currentItem?.amount || 0));
    const shares = currentItem?.shares || {};
    const normViewer = userPhone || '';

    const [alertModal, setAlertModal] = useState<{ title: string; message: string } | null>(null);

    const handleSheetDismissed = useCallback(() => {
        setAlertModal(null);
        onClose();
    }, [onClose]);

    const { isRendered, isClosing, handleClose: handleSafeClose } = useSheetLifecycle(visible, handleSheetDismissed, 220);

    // Helper to check if participant is locked
    const isParticipantLocked = useCallback((p?: WhoHadThisParticipant, shareObj?: any) => {
        const pKey = p ? getWhoHadThisParticipantCanonicalKey(p, organizerPhone) : '';
        return Boolean(
            p?.isLocked ||
            p?.isPaymentLocked ||
            p?.isPaid ||
            p?.status === 'paid' ||
            p?.status === 'processing' ||
            p?.status === 'settled_offline' ||
            p?.status === 'verified' ||
            shareObj?.isLocked ||
            (pKey && currentItem?.shares?.[pKey]?.isLocked)
        );
    }, [currentItem?.shares, organizerPhone]);

    // Existing claimants
    const claimants = useMemo(() => {
        return Object.entries(normalizeShareMap(shares))
            .filter(([_, val]: [string, any]) => getShareFraction(val) > 0.0001)
            .map(([key, val]: [string, any]) => {
                const pObj = participants.find((p) => matchesWhoHadThisParticipant(p, key, organizerPhone));
                const canonicalKey = pObj ? getWhoHadThisParticipantCanonicalKey(pObj, organizerPhone) : key;
                const resolved = resolveWebParticipantIdentity({
                    participant: pObj || { phone: canonicalKey, name: val?.name },
                    viewerPhone: userPhone,
                    organizerPhone,
                    organizerName,
                    organizerPhotoURL,
                    session,
                });
                const isLocked = isParticipantLocked(pObj, val);
                const isMe = Boolean(normViewer && (matchesWhoHadThisParticipant({ phone: key, id: key } as any, normViewer, organizerPhone) || (pObj && matchesWhoHadThisParticipant(pObj, normViewer, organizerPhone))));
                return {
                    phone: canonicalKey,
                    name: isMe ? 'You' : (resolved.canonicalName || pObj?.name || val?.name || 'Friend'),
                    fraction: getShareFraction(val),
                    photoURL: resolved.photoURL || pObj?.photoURL,
                    initials: resolved.initials,
                    mode: String(val?.mode || ''),
                    isLocked,
                };
            });
    }, [isParticipantLocked, normViewer, organizerName, organizerPhone, organizerPhotoURL, participants, session, shares, userPhone]);

    const isUnclaimed = claimants.length === 0;
    const totalClaimedFraction = useMemo(() => {
        return claimants.reduce((sum, c) => sum + c.fraction, 0);
    }, [claimants]);

    const lockedClaimants = useMemo(() => claimants.filter((c) => c.isLocked), [claimants]);
    const lockedFraction = useMemo(() => {
        return lockedClaimants.reduce((sum, c) => sum + c.fraction, 0);
    }, [lockedClaimants]);

    const isFullyLocked = lockedFraction >= 0.999;
    const lockedDollars = Number((lockedFraction * itemAmount).toFixed(2));
    const availableAmount = Math.max(0, Number((itemAmount - lockedDollars).toFixed(2)));
    const unlockedClaimants = useMemo(() => claimants.filter((c) => !c.isLocked), [claimants]);

    const remainingFraction = Math.max(0, 1 - totalClaimedFraction);
    const remainingDollars = Number((remainingFraction * itemAmount).toFixed(2));
    const hasCustomClaimants = claimants.some((c) => c.mode === 'custom');
    const isCustomRemainder = (hasCustomClaimants || remainingFraction > 0.001) && totalClaimedFraction > 0.001 && totalClaimedFraction < 0.999;

    const splitCount = unlockedClaimants.length + 1;
    const joinPortionAmount = useMemo(() => {
        if (itemAmount <= 0 || isFullyLocked) return 0;
        if (isCustomRemainder && remainingDollars > 0) {
            return remainingDollars;
        }
        return Number((availableAmount / Math.max(1, splitCount)).toFixed(2));
    }, [availableAmount, isCustomRemainder, isFullyLocked, itemAmount, remainingDollars, splitCount]);

    const claimantNamesLabel = claimants
        .map((c) => (c.phone === normViewer ? 'You' : c.name))
        .filter(Boolean)
        .join(', ');

    const handleJoinClick = () => {
        if (isFullyLocked || (availableAmount <= 0.001 && lockedClaimants.length > 0)) {
            triggerUiHaptic('notificationWarning');
            setAlertModal({
                title: 'Item Fully Paid',
                message: 'This item has already been paid for in full by locked participants and cannot be joined.',
            });
            return;
        }
        onJoinSplit(currentItem.id, joinPortionAmount, !isCustomRemainder);
        handleSafeClose();
    };

    const handleClaimFullClick = () => {
        if (isFullyLocked || (availableAmount <= 0.001 && lockedClaimants.length > 0)) {
            triggerUiHaptic('notificationWarning');
            setAlertModal({
                title: 'Item Fully Paid',
                message: 'This item has already been paid for in full by locked participants and cannot be joined.',
            });
            return;
        }
        onClaimFull(currentItem.id);
        handleSafeClose();
    };

    if (!isRendered) return null;
    if (!currentItem) return null;

    return (
        <div className={`fixed inset-0 z-[80] bg-slate-950/60 backdrop-blur-sm flex flex-col justify-end sm:items-center sm:justify-center p-0 sm:p-4 overscroll-contain ${
            isClosing ? 'animate-backdrop-exit pointer-events-none' : 'animate-backdrop-enter'
        }`}>
            <div className="absolute inset-0" onClick={handleSafeClose} />
            <div
                className={`relative w-full max-w-lg bg-white rounded-t-[2.25rem] sm:rounded-[2.25rem] flex flex-col overflow-hidden overscroll-contain ${
                    isClosing ? 'animate-sheet-exit' : 'animate-sheet-enter'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Top Pill Handle */}
                <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mt-3 mb-1" />

                {/* Header */}
                <div className="px-6 pt-2 pb-4 border-b border-slate-100/80 flex items-center justify-between">
                    <div className="flex items-center gap-3.5 min-w-0 pr-4">
                        <WebItemIconAvatar name={currentItem.name} iconKey={currentItem.iconKey} size={40} />
                        <div className="min-w-0 flex-1">
                            <h3 className="text-base font-extrabold text-slate-900 truncate leading-tight">
                                {currentItem.name}
                            </h3>
                            <p className="text-xs font-semibold text-slate-500 mt-0.5">
                                Total: ${itemAmount.toFixed(2)}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleSafeClose}
                        className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors shrink-0 cursor-pointer"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4 stroke-[2.2]" />
                    </button>
                </div>

                {/* Body Content */}
                <div className="p-6">
                    {!isUnclaimed ? (
                        /* Case A: Already Shared Item */
                        <div className="space-y-6">
                            <div className="bg-slate-50 rounded-2xl p-6 text-center">
                                {/* Overlapping Claimant Avatars */}
                                <div className="flex items-center justify-center -space-x-2 mb-3">
                                    {claimants.slice(0, 4).map((c, i) => (
                                        <div
                                            key={i}
                                            className={`relative w-9 h-9 rounded-full bg-[#D7E1EA] text-[#475569] border-2 border-white flex items-center justify-center overflow-hidden text-xs font-bold shadow-xs ${
                                                c.isLocked ? 'opacity-60' : ''
                                            }`}
                                        >
                                            {c.photoURL ? (
                                                <img src={c.photoURL} alt={c.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <span>{c.initials || getParticipantInitials(c.name)}</span>
                                            )}
                                            {c.isLocked && (
                                                <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center">
                                                    <Lock className="w-3 h-3 text-white stroke-[2.5]" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {claimants.length > 4 && (
                                        <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 border-2 border-white flex items-center justify-center text-xs font-bold">
                                            +{claimants.length - 4}
                                        </div>
                                    )}
                                </div>

                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                                    Shared by {claimantNamesLabel}
                                </p>
                                <h4 className="text-xl font-black text-slate-900 tracking-tight">
                                    {isFullyLocked ? 'Item fully paid' : isCustomRemainder ? 'Claim remaining?' : 'Join this split?'}
                                </h4>
                                <p className="text-xs font-semibold text-slate-500 mt-1">
                                    {isFullyLocked
                                        ? 'All shares for this item have already been paid for.'
                                        : isCustomRemainder
                                        ? `Claim the remaining $${remainingDollars.toFixed(2)}`
                                        : `Your portion would become $${joinPortionAmount.toFixed(2)}`}
                                </p>
                            </div>

                            {/* Dual CTAs: Custom + Join split */}
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        triggerUiHaptic('selection');
                                        onShareWithSomeone(currentItem.id);
                                    }}
                                    className="flex-1 h-12 min-h-[48px] rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] cursor-pointer"
                                >
                                    <SlidersHorizontal className="w-4 h-4 stroke-[2.2]" />
                                    <span>Custom</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleJoinClick}
                                    className="flex-1 h-12 min-h-[48px] rounded-full bg-slate-950 hover:bg-slate-900 text-white font-bold text-sm flex items-center justify-center transition-all active:scale-[0.98] shadow-sm cursor-pointer"
                                >
                                    {isCustomRemainder ? `Claim · $${joinPortionAmount.toFixed(2)}` : `Join · $${joinPortionAmount.toFixed(2)}`}
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Case B: Unassigned Item (Matches App Screenshot) */
                        <div className="space-y-6">
                            <div className="bg-slate-50 rounded-2xl p-6 text-center">
                                <h4 className="text-xl font-black text-slate-900 tracking-tight">
                                    How much of this was yours?
                                </h4>
                                <p className="text-xs font-semibold text-slate-500 mt-1">
                                    Total item cost is ${itemAmount.toFixed(2)}
                                </p>
                            </div>

                            {/* Dual CTAs: Share + All of it */}
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        triggerUiHaptic('selection');
                                        onShareWithSomeone(currentItem.id);
                                    }}
                                    className="flex-1 h-12 min-h-[48px] rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] cursor-pointer"
                                >
                                    <Users className="w-4 h-4 stroke-[2.2]" />
                                    <span>Share</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClaimFullClick}
                                    className="flex-1 h-12 min-h-[48px] rounded-full bg-slate-950 hover:bg-slate-900 text-white font-bold text-sm flex items-center justify-center transition-all active:scale-[0.98] shadow-sm cursor-pointer"
                                >
                                    All of it · ${itemAmount.toFixed(2)}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Alert Modal Popup */}
            {alertModal && (
                <div className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 animate-backdrop-enter">
                    <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl space-y-4 animate-sheet-enter">
                        <div className="space-y-1.5 text-center">
                            <h4 className="text-base font-extrabold text-slate-900">{alertModal.title}</h4>
                            <p className="text-xs font-medium text-slate-500 leading-relaxed">{alertModal.message}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setAlertModal(null)}
                            className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all active:scale-[0.98] cursor-pointer"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebHowWasThisSharedSheet;

